import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { Box } from '@/shared/ui/box';
import { DatePicker } from '@/shared/ui/date-picker';

import { RangePickerProps } from './range-picker.types';

const RangePickerComponent = ({
	onChangeStartDate,
	onChangeEndDate,
	startDate,
	endDate,
	startDateLabel = '',
	endDateLabel = '',
	marginLeft = 'auto',
	marginRight = 'none',
	marginBottom = '32px',
	maxWidth = 'l',
	minWidth = 'l',
	startDateError,
	endDateError,
	padding,
	flexWrap,
	flex,
	showTimeInput = false,
	dateFormat,
	direction,
	timeCaption = 'Time',
	showTimeSelect = false,
	filterTime,
	highlightDates,
	isNextDisabled = false,
	onKeyDown,
	gap = 24,
	startMinDate,
	startMaxDate,
	endMinDate,
	endMaxDate,
	startDayClassName,
	endDayClassName,
	required = false,
}: RangePickerProps) => {
	const { t } = useTranslation('common');
	const {
		input: { width },
	} = useTheme();
	// Use flex when the range picker is rendered alongside other components.
	const boxWidth = width.full === width[maxWidth] ? maxWidth : Number(width[maxWidth].replace(/px/g, '')) * 2 + gap;

	return (
		<Box
			gap={gap}
			marginLeft={marginLeft}
			marginRight={marginRight}
			marginBottom={marginBottom}
			flexWrap={flexWrap}
			flex={flex}
			padding={padding}
			direction={direction}
			width={boxWidth}
		>
			<DatePicker
				value={startDate}
				onChange={onChangeStartDate}
				startDate={startDate}
				endDate={endDate}
				placeholder={t('datepicker.from.placeholder')}
				label={startDateLabel}
				dateFormat={dateFormat}
				selectsStart
				showTimeInput={showTimeInput}
				maxWidth={maxWidth}
				minWidth={minWidth}
				error={startDateError}
				timeCaption={timeCaption}
				showTimeSelect={showTimeSelect}
				filterTime={filterTime}
				highlightDates={highlightDates}
				isNextDisabled={isNextDisabled}
				onKeyDown={onKeyDown}
				minDate={startMinDate}
				maxDate={startMaxDate}
				dayClassName={startDayClassName}
				required={required}
			/>
			<DatePicker
				value={endDate}
				onChange={onChangeEndDate}
				startDate={startDate}
				endDate={endDate}
				placeholder={t('datepicker.to.placeholder')}
				label={endDateLabel}
				dateFormat={dateFormat}
				selectsEnd
				showTimeInput={showTimeInput}
				maxWidth={maxWidth}
				minWidth={minWidth}
				error={endDateError}
				timeCaption={timeCaption}
				showTimeSelect={showTimeSelect}
				filterTime={filterTime}
				highlightDates={highlightDates}
				isNextDisabled={isNextDisabled}
				onKeyDown={onKeyDown}
				minDate={endMinDate || startDate}
				maxDate={endMaxDate}
				dayClassName={endDayClassName}
				required={required}
			/>
		</Box>
	);
};

export default memo(RangePickerComponent);
